import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Download, LogOut, Users, Loader2, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "hitmax_admin_token";

const authHeaders = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue. Réessaie.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [entries, setEntries] = useState([]);
  const [displayedCount, setDisplayedCount] = useState(null);
  const [demoMatches, setDemoMatches] = useState([]);
  const [todayMatchId, setTodayMatchId] = useState(null);
  const [pinning, setPinning] = useState("");
  const [oddsInputs, setOddsInputs] = useState({ home: "", draw: "", away: "" });
  const [savingOdds, setSavingOdds] = useState(false);
  const [oddsMsg, setOddsMsg] = useState("");
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState(null);
  const [sendingCampaign, setSendingCampaign] = useState(null);
  const [testEmails, setTestEmails] = useState({});
  const [testStatus, setTestStatus] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState("");
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      setUser(false);
      return;
    }
    axios
      .get(`${API}/auth/me`, { headers: authHeaders() })
      .then(({ data }) => setUser(data))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setUser(false);
      });
  }, []);

  useEffect(() => {
    if (!user) return;
    axios
      .get(`${API}/admin/waitlist`, { headers: authHeaders() })
      .then(({ data }) => setEntries(data.entries))
      .catch(() => {});
    axios
      .get(`${API}/admin/stats`, { headers: authHeaders() })
      .then(({ data }) => setStats(data))
      .catch(() => {});
    axios
      .get(`${API}/admin/campaigns`, { headers: authHeaders() })
      .then(({ data }) => setCampaigns(data))
      .catch(() => {});
    axios
      .get(`${API}/waitlist/count`)
      .then(({ data }) => setDisplayedCount(data.count))
      .catch(() => {});
    axios
      .get(`${API}/admin/matches`, { headers: authHeaders() })
      .then(({ data }) => setDemoMatches(data.matches || []))
      .catch(() => {});
    axios
      .get(`${API}/demo/today`)
      .then(({ data }) => setTodayMatchId(data.match?.id || null))
      .catch(() => {});
  }, [user]);

  const sendCampaign = async (endpoint) => {
    setSendingCampaign(endpoint);
    try {
      await axios.post(`${API}/admin/campaigns/${endpoint}`, {}, { headers: authHeaders() });
      const { data } = await axios.get(`${API}/admin/campaigns`, { headers: authHeaders() });
      setCampaigns(data);
    } catch {}
    setSendingCampaign(null);
  };

  const sendTestCampaign = async (endpoint) => {
    const email = (testEmails[endpoint] || "").trim();
    if (!email.includes("@")) {
      setTestStatus((s) => ({ ...s, [endpoint]: "Email invalide" }));
      return;
    }
    setSendingCampaign(`test-${endpoint}`);
    try {
      await axios.post(`${API}/admin/campaigns/${endpoint}/test`, { email }, { headers: authHeaders() });
      setTestStatus((s) => ({ ...s, [endpoint]: `Test envoyé à ${email}` }));
    } catch (err) {
      setTestStatus((s) => ({ ...s, [endpoint]: err.response?.data?.detail || "Échec de l'envoi" }));
    }
    setSendingCampaign(null);
  };

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, form);
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setUser(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { headers: authHeaders() }).catch(() => {});
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
    setEntries([]);
  };

  const exportCsv = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/waitlist/export`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "hitmax-waitlist.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  const inputCls =
    "w-full bg-[#111111] border border-white/10 rounded-sm px-4 py-3 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-[#CCFF00]/60 transition-colors duration-300";

  return (
    <div className="min-h-screen bg-[#050505] text-white font-body">
      <header className="border-b border-white/5 bg-black/40 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <a href="/" data-testid="admin-logo" className="flex items-center">
            <img src="/logo-hitmax.png" alt="HitMax" className="h-7 w-auto" />
          </a>
          {user && (
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 font-mono2 text-xs tracking-widest uppercase text-zinc-400 hover:text-[#CCFF00] transition-colors duration-300"
            >
              <LogOut className="w-4 h-4" />
              Déconnexion
            </button>
          )}
        </div>
      </header>

      {user === null ? (
        <div className="flex items-center justify-center py-40">
          <Loader2 className="w-6 h-6 animate-spin text-[#CCFF00]" />
        </div>
      ) : !user ? (
        <div className="flex items-center justify-center px-6 py-24">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="neon-trace bg-[#0A0A0A] p-8 w-full max-w-md"
          >
            <p className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-[#CCFF00] mb-2">Console admin</p>
            <h1 className="font-display font-black tracking-tight text-3xl mb-8">Connexion</h1>
            <form data-testid="admin-login-form" onSubmit={login} className="space-y-3">
              <input
                data-testid="admin-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email"
                className={inputCls}
              />
              <input
                data-testid="admin-password-input"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Mot de passe"
                className={inputCls}
              />
              <button
                data-testid="admin-login-button"
                type="submit"
                disabled={loading}
                className="w-full bg-[#CCFF00] text-black font-bold uppercase tracking-widest text-sm rounded-sm px-6 py-3.5 hover:bg-[#b3e600] transition-colors duration-300 disabled:opacity-70"
              >
                {loading ? "Connexion…" : "Se connecter"}
              </button>
              {error && (
                <p data-testid="admin-login-error" className="text-xs text-red-400">
                  {error}
                </p>
              )}
            </form>
          </motion.div>
        </div>
      ) : (
        <main data-testid="admin-dashboard" className="max-w-6xl mx-auto px-6 py-12">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
            <div>
              <p className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-[#CCFF00] mb-2">Waitlist</p>
              <h1 className="font-display font-black tracking-tight text-4xl">
                <span data-testid="admin-count">{entries.length}</span> inscrits
              </h1>
              <p data-testid="admin-count-clarification" className="text-zinc-500 text-sm mt-2">
                Inscrits réels{displayedCount != null && <> — <span className="text-[#CCFF00] font-bold">{displayedCount}</span> affichés sur le site (base de lancement incluse)</>}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                data-testid="admin-refresh-predictions-button"
                onClick={async () => {
                  setRefreshing(true);
                  setRefreshMsg("");
                  try {
                    const { data } = await axios.post(`${API}/admin/predictions/refresh`, {}, { headers: authHeaders() });
                    setRefreshMsg(`Cotes en cours de recalcul (${data.purged} caches vidés) — visibles sur le site dans quelques secondes`);
                  } catch {
                    setRefreshMsg("Erreur lors du recalcul. Réessaie.");
                  } finally {
                    setRefreshing(false);
                  }
                }}
                disabled={refreshing}
                className="flex items-center gap-2 border border-[#CCFF00]/40 text-[#CCFF00] font-bold uppercase tracking-widest text-xs rounded-sm px-5 py-3 hover:bg-[#CCFF00]/10 transition-colors duration-300 w-fit disabled:opacity-50"
              >
                {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Recalculer les cotes
              </button>
              <button
                data-testid="admin-export-button"
                onClick={exportCsv}
                className="flex items-center gap-2 bg-[#CCFF00] text-black font-bold uppercase tracking-widest text-xs rounded-sm px-5 py-3 hover:bg-[#b3e600] transition-colors duration-300 w-fit"
              >
                <Download className="w-4 h-4" />
                Exporter le CSV
              </button>
            </div>
            {refreshMsg && (
              <p data-testid="admin-refresh-status" className="text-xs text-[#CCFF00] mt-3">{refreshMsg}</p>
            )}
          </div>

          {demoMatches.length > 0 && (
            <div className="mb-10 rounded-md border border-white/5 bg-[#0A0A0A] p-6" data-testid="admin-pick-section">
              <p className="font-mono2 text-[10px] tracking-widest uppercase text-zinc-500 mb-4">Match mis en avant sur le site</p>
              <div className="space-y-1 max-h-80 overflow-y-auto pr-2">
                {demoMatches.map((m) => (
                  <div key={m.id} className="flex items-center justify-between gap-3 py-2 border-b border-white/5 last:border-0">
                    <div className="min-w-0">
                      <p className="text-white text-sm font-bold truncate">{m.home} vs {m.away}</p>
                      <p className="text-zinc-500 text-xs">{m.league} · {m.kickoff || m.date}</p>
                    </div>
                    {todayMatchId === m.id ? (
                      <div className="shrink-0 flex flex-col items-end gap-2">
                        <span data-testid="admin-pick-current" className="text-[#CCFF00] text-[10px] font-bold uppercase tracking-widest">À l'affiche</span>
                        <div className="flex items-center gap-1" data-testid="admin-odds-form">
                          {["home", "draw", "away"].map((k, i) => (
                            <input
                              key={k}
                              data-testid={`admin-odds-${k}`}
                              value={oddsInputs[k]}
                              onChange={(e) => setOddsInputs({ ...oddsInputs, [k]: e.target.value })}
                              placeholder={["1", "N", "2"][i]}
                              className="w-14 bg-black border border-white/10 rounded-sm px-2 py-1.5 text-white text-xs text-center placeholder-zinc-600 focus:border-[#CCFF00]/50 focus:outline-none"
                            />
                          ))}
                          <button
                            data-testid="admin-odds-save"
                            disabled={savingOdds}
                            onClick={async () => {
                              setSavingOdds(true);
                              setOddsMsg("");
                              try {
                                await axios.post(`${API}/admin/odds-override`, { match_id: m.id, odds: oddsInputs }, { headers: authHeaders() });
                                setOddsMsg("Cote manuelle enregistrée — visible sur le site");
                              } catch {
                                setOddsMsg("Cote invalide");
                              }
                              setSavingOdds(false);
                            }}
                            className="border border-[#CCFF00]/40 text-[#CCFF00] font-bold uppercase tracking-widest text-[10px] rounded-sm px-3 py-1.5 hover:bg-[#CCFF00]/10 transition-colors duration-300 disabled:opacity-50"
                          >
                            {savingOdds ? "..." : "Cote"}
                          </button>
                        </div>
                        {oddsMsg && <p data-testid="admin-odds-status" className="text-[10px] text-zinc-500">{oddsMsg}</p>}
                      </div>
                    ) : (
                      <button
                        data-testid={`admin-pin-${m.id}`}
                        disabled={pinning === m.id}
                        onClick={async () => {
                          setPinning(m.id);
                          try {
                            await axios.post(`${API}/admin/pick-today`, { match_id: m.id }, { headers: authHeaders() });
                            setTodayMatchId(m.id);
                          } catch {}
                          setPinning("");
                        }}
                        className="shrink-0 border border-[#CCFF00]/40 text-[#CCFF00] font-bold uppercase tracking-widest text-[10px] rounded-sm px-3 py-2 hover:bg-[#CCFF00]/10 transition-colors duration-300 disabled:opacity-50"
                      >
                        {pinning === m.id ? "..." : "Mettre en avant"}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {stats && (
            <div className="mb-10 space-y-5">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
                <div className="rounded-md border border-white/5 bg-[#0A0A0A] p-6">
                  <p className="font-mono2 text-[10px] tracking-widest uppercase text-zinc-500 mb-2">Inscrits total</p>
                  <p data-testid="admin-stat-total" className="font-display font-black text-3xl text-white">{stats.total}</p>
                </div>
                <div className="rounded-md border border-white/5 bg-[#0A0A0A] p-6">
                  <p className="font-mono2 text-[10px] tracking-widest uppercase text-zinc-500 mb-2">Parrainages</p>
                  <p data-testid="admin-stat-referrals" className="font-display font-black text-3xl text-[#CCFF00]">{stats.referrals}</p>
                </div>
                <div className="rounded-md border border-[#CCFF00]/25 bg-[#0A0A0A] p-6">
                  <p className="font-mono2 text-[10px] tracking-widest uppercase text-zinc-500 mb-2">Ventes VIP</p>
                  <p data-testid="admin-stat-vip" className="font-display font-black text-3xl text-[#CCFF00]">{stats.vip ?? 0}</p>
                  <p data-testid="admin-stat-vip-revenue" className="font-mono2 text-[10px] tracking-widest text-zinc-500 mt-1">
                    {((stats.vip ?? 0) * 6.99).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })} TTC
                  </p>
                </div>
              </div>
              <div className="rounded-md border border-white/5 bg-[#0A0A0A] p-6">
                <p className="font-mono2 text-[10px] tracking-widest uppercase text-zinc-500 mb-6">Inscriptions — 14 derniers jours</p>
                <div className="h-56" data-testid="admin-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={stats.days}>
                      <XAxis dataKey="date" tick={{ fill: "#52525b", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} stroke="#1f1f1f" tickLine={false} />
                      <Tooltip contentStyle={{ background: "#111111", border: "1px solid #222222", fontSize: 12 }} labelStyle={{ color: "#a1a1aa" }} />
                      <Area type="monotone" dataKey="count" stroke="#CCFF00" fill="#CCFF00" fillOpacity={0.12} strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {campaigns && (
            <div className="space-y-4 mb-10">
              {[
                { key: "j7_reminder", label: "Campagne J-7 — Surclassement VIP 6,99 €", date: campaigns.j7_date, endpoint: "j7" },
                { key: "j1_reminder", label: "Campagne J-1 — Dernière relance + offre VIP", date: campaigns.j1_date, endpoint: "j1" },
                { key: "j0_launch", label: "Campagne J-0 — Lancement + accès à l'app", date: campaigns.j0_date, endpoint: "j0" },
              ].map((c) => {
                const sent = campaigns.campaigns?.find((x) => x.name === c.key);
                return (
                  <div key={c.key} data-testid={`admin-campaign-${c.endpoint}`} className="rounded-md border border-[#CCFF00]/20 bg-[#0A0A0A] p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <p className="font-mono2 text-[10px] tracking-widest uppercase text-[#CCFF00] mb-2">{c.label}</p>
                      <p className="text-sm text-zinc-300">
                        {sent
                          ? `Envoyée le ${new Date(sent.sent_at).toLocaleDateString("fr-FR")} à ${sent.sent_count} inscrits`
                          : `Envoi automatique programmé le ${c.date}`}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      <button
                        data-testid={`admin-send-${c.endpoint}-button`}
                        onClick={() => sendCampaign(c.endpoint)}
                        disabled={!!sent || sendingCampaign === c.endpoint}
                        className="bg-[#CCFF00] text-black font-bold uppercase tracking-widest text-xs rounded-sm px-5 py-3 hover:bg-[#b3e600] transition-colors duration-300 disabled:opacity-40"
                      >
                        {sendingCampaign === c.endpoint ? "Envoi…" : sent ? "Déjà envoyée" : "Envoyer maintenant"}
                      </button>
                      <div className="flex gap-2">
                        <input
                          data-testid={`admin-test-email-${c.endpoint}`}
                          type="email"
                          value={testEmails[c.endpoint] || ""}
                          onChange={(e) => setTestEmails((s) => ({ ...s, [c.endpoint]: e.target.value }))}
                          placeholder="ton@email.com"
                          className="w-40 bg-[#111111] border border-white/10 rounded-sm px-3 py-2 text-xs text-white placeholder:text-zinc-600 outline-none focus:border-[#CCFF00]/50"
                        />
                        <button
                          data-testid={`admin-test-${c.endpoint}-button`}
                          onClick={() => sendTestCampaign(c.endpoint)}
                          disabled={sendingCampaign === `test-${c.endpoint}`}
                          className="border border-[#CCFF00]/40 text-[#CCFF00] font-bold uppercase tracking-widest text-xs rounded-sm px-3 py-2 hover:bg-[#CCFF00]/10 transition-colors duration-300 disabled:opacity-40"
                        >
                          {sendingCampaign === `test-${c.endpoint}` ? "…" : "Test"}
                        </button>
                      </div>
                      {testStatus[c.endpoint] && (
                        <p data-testid={`admin-test-status-${c.endpoint}`} className="text-[11px] text-zinc-400">{testStatus[c.endpoint]}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="rounded-md border border-white/5 bg-[#0A0A0A] overflow-hidden">
            <div className="overflow-x-auto">
              <table data-testid="admin-table" className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/5 font-mono2 text-[10px] tracking-widest uppercase text-zinc-500">
                    <th className="text-left px-5 py-4">Prénom</th>
                    <th className="text-left px-5 py-4">Nom</th>
                    <th className="text-left px-5 py-4">Email</th>
                    <th className="text-left px-5 py-4">Langue</th>
                    <th className="text-left px-5 py-4">VIP</th>
                    <th className="text-left px-5 py-4">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e, i) => (
                    <tr key={e.email} data-testid={`admin-row-${i}`} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors duration-200">
                      <td className="px-5 py-3.5 text-white">{e.first_name}</td>
                      <td className="px-5 py-3.5 text-zinc-300">{e.last_name}</td>
                      <td className="px-5 py-3.5 text-zinc-400">{e.email}</td>
                      <td className="px-5 py-3.5 text-zinc-500 uppercase font-mono2 text-xs">{e.lang || "fr"}</td>
                      <td className="px-5 py-3.5">
                        {e.vip ? (
                          <span data-testid={`admin-vip-badge-${i}`} className="font-mono2 text-[10px] tracking-widest px-2 py-1 rounded-sm bg-[#CCFF00]/10 border border-[#CCFF00]/40 text-[#CCFF00]">VIP</span>
                        ) : (
                          <span className="text-zinc-700">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-zinc-500 font-mono2 text-xs">
                        {e.created_at ? new Date(e.created_at).toLocaleDateString("fr-FR") : "—"}
                      </td>
                    </tr>
                  ))}
                  {entries.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-5 py-10 text-center text-zinc-600">
                        <Users className="w-5 h-5 mx-auto mb-2" />
                        Aucun inscrit pour le moment.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      )}
    </div>
  );
}
